import { defineStore } from 'pinia'
import type { Ref } from 'vue'
import type { DiningArea, DiningAreaForm, DiningAreaWithTableCount } from '~/types/diningAreas'

export const useDiningAreasStore = defineStore('diningAreas', () => {
  const log = useLogger('store:diningAreas')
  const { get, post, put, delete: del } = useAuthenticatedFetch()
  const businessStore = useBusinessStore()
  const { businessId } = storeToRefs(businessStore)

  // State
  const diningAreas: Ref<DiningArea[]> = ref([])
  const diningAreasWithTableCount: Ref<DiningAreaWithTableCount[]> = ref([])
  const loading: Ref<boolean> = ref(false)

  // Filter states
  const searchTerm: Ref<string> = ref('')
  const showInactive: Ref<boolean> = ref(false)

  // Core data fetching
  async function fetchDiningAreas(): Promise<void> {
    if (!businessId.value) return

    loading.value = true
    try {
      const queryParam = showInactive.value ? '?include_inactive=true' : ''
      const res = await get(`/api/business/${businessId.value}/dining-areas${queryParam}`)
      diningAreas.value = res.data || []
    }
    catch (error: any) {
      log.error('Error fetching dining areas:', error)

      if (error.name === 'OfflineError') {
        useErrorToast('App is offline - using cached dining areas')
      }
      else if (error.name === 'AuthenticationRequired') {
        useErrorToast('Please sign in to load dining areas')
      }
      else {
        useErrorToast('Failed to load dining areas')
      }
    }
    finally {
      loading.value = false
    }
  }

  async function fetchDiningAreaById(areaId: number): Promise<DiningArea | null> {
    if (!businessId.value) return null

    try {
      const res = await get(`/api/business/${businessId.value}/dining-areas/${areaId}`)
      return res.data || null
    }
    catch (error: any) {
      log.error('Error fetching dining area:', error)

      if (error.name === 'OfflineError') {
        useErrorToast('App is offline')
      }
      else if (error.name === 'AuthenticationRequired') {
        useErrorToast('Please sign in')
      }
      else {
        useErrorToast('Failed to load dining area')
      }
      return null
    }
  }

  async function fetchDiningAreasWithTableCount(): Promise<void> {
    if (!businessId.value) return

    loading.value = true
    try {
      const res = await get(`/api/business/${businessId.value}/dining-areas/with-table-count`)
      diningAreasWithTableCount.value = res.data || []
    }
    catch (error: any) {
      log.error('Error fetching dining areas with table count:', error)

      if (error.name === 'OfflineError') {
        useErrorToast('App is offline - using cached data')
      }
      else if (error.name === 'AuthenticationRequired') {
        useErrorToast('Please sign in to load data')
      }
      else {
        useErrorToast('Failed to load dining areas')
      }
    }
    finally {
      loading.value = false
    }
  }

  // CRUD operations
  async function createDiningArea(formData: DiningAreaForm): Promise<boolean> {
    if (!businessId.value) {
      useErrorToast('Business ID not found')
      return false
    }

    try {
      const res = await post(`/api/business/${businessId.value}/dining-areas`, formData)

      if (res.success) {
        useSuccessToast('Dining area created successfully')
        await fetchDiningAreas()
        return true
      }
      else {
        const errorMsg = res.message ? `Failed to create dining area: ${res.message}` : 'Failed to create dining area'
        useErrorToast(errorMsg)
        return false
      }
    }
    catch (error: any) {
      log.error('Error creating dining area:', error)

      if (error.name === 'OfflineError') {
        useErrorToast('Cannot create dining area while offline')
      }
      else if (error.name === 'AuthenticationRequired') {
        useErrorToast('Please sign in to create dining areas')
      }
      else {
        const errorMessage = error?.data?.message || error?.message || 'Unknown error'
        useErrorToast(`Failed to create dining area: ${errorMessage}`)
      }
      return false
    }
  }

  async function updateDiningArea(areaId: number, formData: Partial<DiningAreaForm>): Promise<boolean> {
    if (!businessId.value) {
      useErrorToast('Business ID not found')
      return false
    }

    try {
      const res = await put(`/api/business/${businessId.value}/dining-areas/${areaId}`, formData)

      if (res.success) {
        useSuccessToast('Dining area updated successfully')
        await fetchDiningAreas()
        return true
      }
      else {
        const errorMsg = res.message ? `Failed to update dining area: ${res.message}` : 'Failed to update dining area'
        useErrorToast(errorMsg)
        return false
      }
    }
    catch (error: any) {
      log.error('Error updating dining area:', error)

      if (error.name === 'OfflineError') {
        useErrorToast('Cannot update dining area while offline')
      }
      else if (error.name === 'AuthenticationRequired') {
        useErrorToast('Please sign in to update dining areas')
      }
      else {
        const errorMessage = error?.data?.message || error?.message || 'Unknown error'
        useErrorToast(`Failed to update dining area: ${errorMessage}`)
      }
      return false
    }
  }

  async function activateDiningArea(areaId: number): Promise<boolean> {
    if (!businessId.value) {
      useErrorToast('Business ID not found')
      return false
    }

    try {
      const res = await put(`/api/business/${businessId.value}/dining-areas/${areaId}`, { active: true })

      if (res.success) {
        useSuccessToast('Dining area activated successfully')
        await fetchDiningAreas()
        return true
      }
      else {
        useErrorToast('Failed to activate dining area')
        return false
      }
    }
    catch (error: any) {
      log.error('Error activating dining area:', error)
      useErrorToast('Failed to activate dining area')
      return false
    }
  }

  async function deleteDiningArea(areaId: number, hardDelete = false): Promise<boolean> {
    if (!businessId.value) {
      useErrorToast('Business ID not found')
      return false
    }

    try {
      const queryParam = hardDelete ? '?hard=true' : ''
      const res = await del(`/api/business/${businessId.value}/dining-areas/${areaId}${queryParam}`)

      if (res.success) {
        useSuccessToast(hardDelete ? 'Dining area deleted successfully' : 'Dining area deactivated successfully')
        await fetchDiningAreas()
        return true
      }
      else {
        useErrorToast('Failed to delete dining area')
        return false
      }
    }
    catch (error: any) {
      log.error('Error deleting dining area:', error)

      if (error.name === 'OfflineError') {
        useErrorToast('Cannot delete dining area while offline')
      }
      else if (error.name === 'AuthenticationRequired') {
        useErrorToast('Please sign in to delete dining areas')
      }
      else {
        useErrorToast('Failed to delete dining area')
      }
      return false
    }
  }

  async function reorderDiningAreas(orderMap: Record<number, number>): Promise<boolean> {
    if (!businessId.value) {
      useErrorToast('Business ID not found')
      return false
    }

    try {
      const res = await post(`/api/business/${businessId.value}/dining-areas/reorder`, { orderMap })

      if (res.success) {
        useSuccessToast('Dining areas reordered successfully')
        await fetchDiningAreas()
        return true
      }
      else {
        useErrorToast('Failed to reorder dining areas')
        return false
      }
    }
    catch (error: any) {
      log.error('Error reordering dining areas:', error)

      if (error.name === 'OfflineError') {
        useErrorToast('Cannot reorder dining areas while offline')
      }
      else if (error.name === 'AuthenticationRequired') {
        useErrorToast('Please sign in to reorder dining areas')
      }
      else {
        useErrorToast('Failed to reorder dining areas')
      }
      return false
    }
  }

  // Computed properties for filtering
  const filteredDiningAreas = computed(() => {
    let filtered = diningAreas.value

    // Filter by search term
    if (searchTerm.value.trim()) {
      const term = searchTerm.value.toLowerCase()
      filtered = filtered.filter(area =>
        area.name.toLowerCase().includes(term)
        || area.description?.toLowerCase().includes(term),
      )
    }

    // Filter by active status
    if (!showInactive.value) {
      filtered = filtered.filter(area => area.active)
    }

    return filtered
  })

  const totalDiningAreas = computed(() => diningAreas.value.length)

  const activeDiningAreas = computed(() =>
    diningAreas.value.filter(area => area.active).length,
  )

  const inactiveDiningAreas = computed(() =>
    diningAreas.value.filter(area => !area.active).length,
  )

  // Utility functions
  function getDiningAreaById(id: number): DiningArea | undefined {
    return diningAreas.value.find(area => area.id === id)
  }

  function getDiningAreaByName(name: string): DiningArea | undefined {
    return diningAreas.value.find(area => area.name === name)
  }

  // Search and filter methods
  function setSearchTerm(term: string): void {
    searchTerm.value = term
  }

  function toggleShowInactive(): void {
    showInactive.value = !showInactive.value
    fetchDiningAreas()
  }

  function clearFilters(): void {
    searchTerm.value = ''
    showInactive.value = false
  }

  // Reset method to clear all state
  function reset(): void {
    diningAreas.value = []
    diningAreasWithTableCount.value = []
    loading.value = false
    searchTerm.value = ''
    showInactive.value = false
  }

  return {
    // State
    diningAreas,
    diningAreasWithTableCount,
    loading,
    searchTerm,
    showInactive,

    // Computed
    filteredDiningAreas,
    totalDiningAreas,
    activeDiningAreas,
    inactiveDiningAreas,

    // Core methods
    fetchDiningAreas,
    fetchDiningAreaById,
    fetchDiningAreasWithTableCount,
    createDiningArea,
    updateDiningArea,
    activateDiningArea,
    deleteDiningArea,
    reorderDiningAreas,

    // Utility methods
    getDiningAreaById,
    getDiningAreaByName,

    // Filter methods
    setSearchTerm,
    toggleShowInactive,
    clearFilters,

    // Reset method
    reset,
  }
}, {
  persist: {
    key: 'diningAreas',
    storage: localStorage,
    paths: ['diningAreas', 'diningAreasWithTableCount'],
  },
})

export const createDiningAreasStore = async (businessIdParam: string) => {
  const diningAreasStore = useDiningAreasStore()
  const businessStore = useBusinessStore()

  businessStore.setBusinessId(businessIdParam)
  await diningAreasStore.fetchDiningAreas()

  return diningAreasStore
}
